"use client"

import { motion, useMotionValue, useScroll, useSpring, useTransform, type MotionValue } from "framer-motion"
import { BookOpen, Bookmark, Copy, Layers3, MessageSquare, MousePointer2, PanelsTopLeft, Search, Sparkles, Tags, Video } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"

type AppItem = { name: string; category: string; tone?: string }

type ProductPreviewProps = {
  apps: AppItem[]
  screenshots: string[]
  className?: string
}

type FlowStep = { title: string; description: string; meta?: string }
type WorkflowItem = { title: string; description: string; meta?: string }
type Testimonial = { quote: string; name: string; role: string }

const defaultApps: AppItem[] = [
  { name: "Airbnb", category: "Travel" },
  { name: "Wise", category: "Finance" },
  { name: "Shop", category: "Commerce" },
  { name: "Loom", category: "Collaboration" },
  { name: "Notion", category: "Productivity" },
  { name: "Spotify", category: "Media" },
  { name: "Nike", category: "Retail" },
  { name: "ChatGPT", category: "AI" },
  { name: "Dropbox", category: "Storage" },
  { name: "Headspace", category: "Wellness" },
  { name: "Pinterest", category: "Discovery" },
  { name: "Uber", category: "Mobility" },
]

const positions = [
  "left-[5%] top-[8%]",
  "left-[17%] top-[19%]",
  "left-[33%] top-[5%]",
  "right-[33%] top-[6%]",
  "right-[17%] top-[19%]",
  "right-[5%] top-[8%]",
  "left-[7%] top-[43%]",
  "right-[7%] top-[43%]",
  "left-[18%] bottom-[12%]",
  "left-[34%] bottom-[4%]",
  "right-[34%] bottom-[5%]",
  "right-[18%] bottom-[12%]",
]

const tones = [
  "bg-sky-500/12 text-sky-700",
  "bg-emerald-500/12 text-emerald-700",
  "bg-violet-500/12 text-violet-700",
  "bg-amber-500/14 text-amber-700",
  "bg-rose-500/12 text-rose-700",
  "bg-cyan-500/12 text-cyan-700",
]

function FloatingAppIcon({ app, index, progress, pointerX, pointerY }: { app: AppItem; index: number; progress: ReturnType<typeof useScroll>["scrollYProgress"]; pointerX: MotionValue<number>; pointerY: MotionValue<number> }) {
  const offset = index * 0.018
  const opacity = useTransform(progress, [0, 0.08 + offset, 0.42 + offset, 0.82], [0.36, 0.58, 0.44, 0.12])
  const scale = useTransform(progress, [0, 0.35, 0.82], [1, 1.08, 0.78])
  const y = useTransform(progress, [0, 1], [0, -70 - index * 3])
  const x = useTransform(progress, [0, 1], [0, index % 2 === 0 ? -24 : 24])
  const depth = 0.18 + (index % 4) * 0.08
  const driftX = useTransform([x, pointerX], ([scrollX, cursorOffset]) => Number(scrollX) + Number(cursorOffset) * depth)
  const driftY = useTransform([y, pointerY], ([scrollY, cursorOffset]) => Number(scrollY) + Number(cursorOffset) * depth)
  const tone = app.tone ?? tones[index % tones.length]

  return (
    <motion.div
      data-floating-app-icon
      style={{ opacity, scale, x: driftX, y: driftY }}
      whileHover={{ opacity: 1, scale: 1.18, zIndex: 20 }}
      transition={{ type: "spring", stiffness: 360, damping: 24 }}
      className={cn("group pointer-events-auto absolute hidden size-16 cursor-pointer items-center justify-center rounded-card border border-border/60 bg-card/72 shadow-[0_18px_60px_rgba(0,0,0,0.10)] backdrop-blur-xl transition-shadow hover:shadow-[0_24px_80px_rgba(0,0,0,0.18)] md:flex", positions[index % positions.length])}
      aria-label={`${app.name} ${app.category} reference`}
    >
      <span className={cn("flex size-11 items-center justify-center rounded-xl text-sm font-semibold", tone)}>{app.name.slice(0, 2).toUpperCase()}</span>
      <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded-full border border-border/70 bg-background/95 px-2.5 py-1 text-[10px] font-medium text-foreground opacity-0 shadow-sm transition-opacity duration-200 group-hover:opacity-100">{app.name}</span>
    </motion.div>
  )
}

function ProductScreenCard({ label, index }: { label: string; index: number }) {
  return (
    <article className="min-w-36 rounded-card border border-border/70 bg-background p-2 shadow-sm sm:min-w-44">
      <div className="rounded-[1rem] border border-border/70 bg-card p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="size-2 rounded-full bg-primary" />
          <span className="h-2 w-10 rounded-full bg-muted" />
        </div>
        <div className="mt-4 grid gap-2">
          <span className={cn("h-14 rounded-card", index % 4 === 0 ? "bg-primary/14" : index % 4 === 1 ? "bg-emerald-500/12" : index % 4 === 2 ? "bg-violet-500/12" : "bg-amber-500/14")} />
          <span className="h-2.5 rounded-full bg-muted" />
          <span className="h-2.5 w-2/3 rounded-full bg-muted" />
        </div>
      </div>
      <p className="mt-2 truncate text-xs text-muted-foreground">{label}</p>
    </article>
  )
}

export function DesignReferenceHeroMotionIsland({ apps = defaultApps, screenshots, className }: ProductPreviewProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const cursorX = useMotionValue(0)
  const cursorY = useMotionValue(0)
  const springX = useSpring(cursorX, { stiffness: 80, damping: 22, mass: 0.4 })
  const springY = useSpring(cursorY, { stiffness: 80, damping: 22, mass: 0.4 })
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] })
  const previewY = useTransform(scrollYProgress, [0, 0.35, 1], [90, 0, -120])
  const previewScale = useTransform(scrollYProgress, [0, 0.38, 1], [0.94, 1, 0.96])
  const previewOpacity = useTransform(scrollYProgress, [0, 0.12, 0.88, 1], [0.45, 1, 1, 0.72])
  const activeScreenshots = screenshots.length > 0 ? screenshots : ["Profile", "Wallet", "Welcome", "Account setup", "Home", "Paywall", "Checkout", "Collections"]

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      cursorX.set((event.clientX - window.innerWidth / 2) * 0.014)
      cursorY.set((event.clientY - window.innerHeight / 2) * 0.014)
    }

    const handlePointerLeave = () => {
      cursorX.set(0)
      cursorY.set(0)
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerleave", handlePointerLeave)
    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerleave", handlePointerLeave)
    }
  }, [cursorX, cursorY])

  return (
    <div
      ref={sectionRef}
      className={cn("pointer-events-auto relative z-10 mx-auto mt-14 w-full max-w-6xl", className)}
    >
      <motion.div aria-hidden="true" style={{ x: springX, y: springY }} className="pointer-events-auto absolute inset-x-0 -top-[34rem] z-0 h-[38rem] overflow-hidden">
        {apps.slice(0, 12).map((app, index) => <FloatingAppIcon key={`${app.name}-${index}`} app={app} index={index} progress={scrollYProgress} pointerX={springX} pointerY={springY} />)}
      </motion.div>
      <div className="pointer-events-none absolute inset-x-0 -top-[34rem] z-10 mx-auto h-[30rem] max-w-4xl bg-[radial-gradient(ellipse_at_center,hsl(var(--background)/0.9)_0%,hsl(var(--background)/0.76)_34%,transparent_68%)]" />
      <motion.div data-design-reference-product-preview style={{ y: previewY, scale: previewScale, opacity: previewOpacity }} className="pointer-events-auto relative z-20 overflow-hidden rounded-panel border border-border/70 bg-card p-3 shadow-[0_34px_120px_rgba(0,0,0,0.16)] sm:rounded-panel sm:p-4">
        <div className="rounded-card border border-border/70 bg-background p-4 sm:rounded-panel sm:p-6">
          <div className="flex flex-col gap-4 border-b border-border/70 pb-4 md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-card bg-primary/10 text-primary"><Search className="size-5" /></span>
              <div className="min-w-0 text-left">
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Reference browser</p>
                <p className="truncate text-sm font-medium text-foreground">Search onboarding, paywalls, checkout, profiles</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {["Screens", "Flows", "UI Elements"].map((label, index) => <span key={label} className={cn("inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs", index === 0 ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}><Tags className="size-3.5" />{label}</span>)}
            </div>
          </div>
          <div className="grid gap-4 pt-4 lg:grid-cols-[0.7fr_1.3fr]">
            <div className="grid content-start gap-3 text-left">
              <article className="rounded-card border border-border/70 bg-card p-4">
                <div className="flex items-center gap-3"><Layers3 className="size-5 text-primary" /><span className="text-sm font-medium text-foreground">572K+ screens</span></div>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">Dense references grouped by product job and interface pattern.</p>
              </article>
              <article className="rounded-card border border-border/70 bg-card p-4">
                <div className="flex items-center gap-3"><MousePointer2 className="size-5 text-primary" /><span className="text-sm font-medium text-foreground">130K+ flows</span></div>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">Walk through journeys instead of guessing from single screenshots.</p>
              </article>
            </div>
            <div className="flex gap-3 overflow-hidden rounded-card bg-muted/42 p-3">
              {[...activeScreenshots, ...activeScreenshots].slice(0, 10).map((label, index) => <ProductScreenCard key={`${label}-${index}`} label={label} index={index} />)}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export function DesignReferenceFlowMotionIsland({ steps }: { steps: FlowStep[] }) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] })
  const previewY = useTransform(scrollYProgress, [0, 0.5, 1], [70, 0, -70])
  const previewRotate = useTransform(scrollYProgress, [0, 1], [-4, 4])
  const icons = [Video, MousePointer2, PanelsTopLeft]
  const activeStep = steps[activeIndex] ?? steps[0]

  return (
    <div ref={sectionRef} className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
      <motion.div style={{ y: previewY, rotate: previewRotate }} className="relative order-2 overflow-hidden rounded-panel border border-background/16 bg-background/10 p-4 shadow-[0_34px_100px_rgba(0,0,0,0.32)] backdrop-blur lg:order-1">
        <div className="rounded-card bg-background p-3 text-foreground">
          <div className="flex items-center justify-between border-b border-border/70 pb-3">
            <div className="flex items-center gap-2"><span className="size-2.5 rounded-full bg-primary" /><span className="h-2 w-28 rounded-full bg-muted" /></div>
            <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">{activeStep?.meta ?? "Flow"}</span>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {["Entry", "Decision", "Complete"].map((label, index) => <motion.article key={label} animate={{ y: activeIndex === index ? -8 : 0, opacity: activeIndex === index ? 1 : 0.72 }} className="rounded-card border border-border/70 bg-card p-3"><div className={cn("h-28 rounded-xl", index === 0 ? "bg-primary/14" : index === 1 ? "bg-emerald-500/12" : "bg-violet-500/12")} /><p className="mt-3 text-xs font-medium text-foreground">{label}</p><span className="mt-2 block h-2 w-2/3 rounded-full bg-muted" /></motion.article>)}
          </div>
          <div className="mt-4 flex items-center gap-2 overflow-hidden rounded-card bg-muted p-2">
            {[0, 1, 2, 3, 4, 5].map((frame) => <motion.span key={frame} animate={{ opacity: frame <= activeIndex + 2 ? 1 : 0.36, scaleX: frame === activeIndex + 1 ? 1.35 : 1 }} className="h-2 flex-1 rounded-full bg-foreground origin-left" />)}
          </div>
        </div>
      </motion.div>
      <div className="order-1 grid gap-4 lg:order-2">
        {steps.map((step, index) => {
          const Icon = icons[index % icons.length]
          return (
            <motion.article
              key={step.title}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              tabIndex={0}
              whileHover={{ x: -10, scale: 1.02 }}
              whileFocus={{ x: -10, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className={cn("group rounded-panel border p-6 outline-none backdrop-blur transition-all duration-300 hover:-translate-x-2 hover:scale-[1.02] hover:shadow-[0_24px_80px_rgba(0,0,0,0.24)]", activeIndex === index ? "border-primary/60 bg-background/16" : "border-background/16 bg-background/10 hover:border-background/32")}
            >
              <div className="mb-8 flex items-center justify-between gap-4"><span className="flex size-12 items-center justify-center rounded-card bg-background/10 text-primary transition-transform group-hover:rotate-6 group-hover:scale-110"><Icon className="size-5" /></span>{step.meta && <span className="rounded-full bg-background/10 px-3 py-1 text-xs text-background/64">{step.meta}</span>}</div>
              <h3 className="font-heading text-3xl font-light text-background">{step.title}</h3>
              <p className="mt-3 text-sm leading-6 text-background/66">{step.description}</p>
            </motion.article>
          )
        })}
      </div>
    </div>
  )
}

export function DesignReferenceWorkflowMotionGrid({ items }: { items: WorkflowItem[] }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const icons = [Copy, Bookmark, MessageSquare, BookOpen]

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {items.map((item, index) => {
        const Icon = icons[index % icons.length]
        return (
          <motion.article
            key={item.title}
            onMouseEnter={() => setActiveIndex(index)}
            onFocus={() => setActiveIndex(index)}
            tabIndex={0}
            whileHover={{ y: -14, scale: 1.025 }}
            whileFocus={{ y: -14, scale: 1.025 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className={cn("group relative h-full overflow-hidden rounded-panel border border-border/70 bg-card p-6 shadow-sm outline-none transition-all duration-300 hover:-translate-y-3 hover:scale-[1.025] hover:shadow-[0_24px_80px_rgba(0,0,0,0.14)]", activeIndex === index && "border-primary/40")}
          >
            <motion.span animate={{ x: activeIndex === index ? 0 : -90, opacity: activeIndex === index ? 1 : 0 }} className="absolute inset-x-6 top-0 h-1 rounded-full bg-primary" />
            <span className="flex size-12 items-center justify-center rounded-card bg-primary/10 text-primary transition-transform group-hover:rotate-6 group-hover:scale-110"><Icon className="size-6" /></span>
            <p className="mt-10 text-xs uppercase tracking-[0.16em] text-muted-foreground">{item.meta ?? `0${index + 1}`}</p>
            <h3 className="mt-3 text-xl font-medium text-foreground">{item.title}</h3>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{item.description}</p>
            <motion.div animate={{ opacity: activeIndex === index ? 1 : 0.35, y: activeIndex === index ? -4 : 0 }} className="mt-6 grid gap-2 rounded-card bg-muted/70 p-3">
              <span className="h-2 rounded-full bg-foreground/20" />
              <span className="h-2 w-2/3 rounded-full bg-foreground/14" />
            </motion.div>
          </motion.article>
        )
      })}
    </div>
  )
}

export function DesignReferenceTestimonialsMotionWall({ testimonials }: { testimonials: Testimonial[] }) {
  const [activeIndex, setActiveIndex] = useState(0)

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {testimonials.map((testimonial, index) => (
        <motion.figure
          key={testimonial.name}
          onMouseEnter={() => setActiveIndex(index)}
          onFocus={() => setActiveIndex(index)}
          tabIndex={0}
          whileHover={{ y: -12, rotate: index % 2 === 0 ? -1 : 1 }}
          whileFocus={{ y: -12, rotate: index % 2 === 0 ? -1 : 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className={cn("group relative h-full overflow-hidden rounded-panel border border-border/70 bg-card p-6 shadow-sm outline-none transition-all duration-300 hover:-translate-y-3 hover:scale-[1.02] hover:shadow-[0_24px_80px_rgba(0,0,0,0.12)]", activeIndex === index && "border-primary/40")}
        >
          <motion.div animate={{ scale: activeIndex === index ? 1.25 : 1, opacity: activeIndex === index ? 0.16 : 0.06 }} className="absolute -right-12 -top-12 size-36 rounded-full bg-primary" />
          <Sparkles className="relative size-5 text-primary transition-transform group-hover:rotate-12 group-hover:scale-110" />
          <blockquote className="relative mt-8 text-lg leading-8 text-foreground">&quot;{testimonial.quote}&quot;</blockquote>
          <figcaption className="relative mt-8">
            <p className="font-medium text-foreground">{testimonial.name}</p>
            <p className="text-sm text-muted-foreground">{testimonial.role}</p>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  )
}
